import { defineComponent as _defineComponent } from 'vue'
import { MonetizationType, type Maybe, SimilarTitlesEdge } from '@/@types/graphql-types';
import type { ThumborOptions } from '@/filters/thumbor';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { CatalogueItem } from '@/helpers/promotion-helper';
import type { BuyBoxFilterOptions } from '@/stores/user.store';

import ImpressionPixel from '@/features/sponsored-recommendations/components/ImpressionPixel.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import TitleScoreList from '@/components/TitleScoreList.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import PromotionRowCTA from '@/features/buybox/components/list/PromotionRowCTA.vue';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import DisclaimerButton from '@/features/buybox/components/DisclaimerButton.vue';

import { ScoringProvider } from '@/helpers/scoring-helper';
import { ClickoutUrl, getClickoutUrlContextsArray } from '@/helpers/clickout-helper';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { BuyboxParam, OfferLocation } from '@/helpers/offer-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { getPrimeAssociatesSuffix, primeImpactCountries } from '@/helpers/promotion-helper';

import { useQTVCompositionId } from '@/features/buybox/composables/useQTV';
import { getDisneyImpressionUrl } from '@/helpers/provider-promotion';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { useSingleQTV } from '@/features/buybox/composables/useSingleQTV';

import { useI18n } from '@/helpers/composables/useI18n';
import { useAppStore, useLanguageStore } from '@/helpers/composables/useStores';

import Vue, { computed, inject, ref } from 'vue';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { useInjectAdServerIds } from '@/helpers/composables/useAdServer';
import { usePrimeSQTVExp } from '@/components/experiments/PrimeSQTVExp';


export default /*#__PURE__*/_defineComponent({
  __name: 'GridSQTV',
  props: {
    title: null,
    catalogueItem: null,
    additionalContexts: null,
    providerShortName: { default: null },
    swiperIndex: null,
    displayPromotedTag: { type: Boolean, default: true },
    hideFreeTrialTag: { type: Boolean },
    customTargetTitle: null,
    hideRatings: { type: Boolean },
    customTags: null,
    source: { default: 'sqtv' }
  },
  emits: ["onPosterClickOut", "imageError", "onWatchNowClick"],
  setup(__props: any) {

const props = __props as { title: TitleDetail, catalogueItem: CatalogueItem | null, additionalContexts: SnowplowContext[], providerShortName: string | null, swiperIndex?: number, displayPromotedTag: boolean, hideFreeTrialTag?: boolean, customTargetTitle?: TitleDetail, hideRatings?: boolean, customTags?: string[], source: 'sqtv' | 'sr_sqtv' };





const { country, webLocale } = useLanguageStore();
const { xServeTrack } = useAppStore();
const { tc } = useI18n();

const sqtvClickoutType = computed(() => (props.source === 'sr_sqtv' ? 'sr_sqtv_watch_now' : props.source));
const { clickoutType, placement } = getClickoutTrackingPlacement(sqtvClickoutType.value);

const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | undefined>(undefined));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));

const adServerIds = useInjectAdServerIds();

/** impression tracking for Disney+ */
const mountImpressionPixel = ref(false);

const providerShortName = computed(() => props.providerShortName ?? props.catalogueItem?.shortName ?? '');

const isDisneyPlusSQTV = computed(() => providerShortName.value === 'dnp');
const disneyPlusImpressionPixel = computed(() =>
	isDisneyPlusSQTV.value ? getDisneyImpressionUrl('dnp', webLocale.value) : null
);

const {
	similarTitle: _similarTitle,
	loading,
	sqtvBannerAndDisclaimer,
} = useSingleQTV(() => props.title, providerShortName, true);

const similarTitle = computed(() => {
	if (props.customTargetTitle) {
		// Wrap direct TitleDetail in edge structure for consistency
		return { node: props.customTargetTitle };
	}

	return _similarTitle.value;
});

const scores = computed(() => similarTitle.value?.node?.content?.scoring ?? {});

const excludedScores = computed(() => {
	const excluded = [];

	if ((scores.value?.imdbScore || 0) < 7) excluded.push(ScoringProvider.IMDB);
	if ((scores.value?.jwRating || 0) < 0.7) excluded.push(ScoringProvider.TMDB);
	if ((scores.value?.tomatoMeter || 0) < 70) excluded.push(ScoringProvider.ROTTEN_TOMATOES);

	return excluded.length === 3 ? excluded.slice(0, 1) : excluded;
});

const sourcePosterUrl = computed(() => props.title?.content?.posterUrl || '');
const posterUrl = computed(() => similarTitle.value?.node?.content?.posterUrl || '');
const backdropUrl = computed(() => similarTitle.value?.node?.content?.backdrops?.[0]?.backdropUrl || '');
const votesNumber = computed(() => similarTitle.value?.node?.content?.interactions?.votesNumber || 0);

const offer = computed(() => {
	// For SR campaigns with customTargetTitle, get offer from catalogueItem
	if (props.customTargetTitle && props.catalogueItem?.offer) {
		return props.catalogueItem.offer(null, country.value, true);
	}

	// For regular SQTV, get offers from the similar title
	const offers = similarTitle.value?.node?.offers || [];

	return offers[0];
});

const jwRawEntityId = computed(() => similarTitle.value?.node.id ?? 'nsid');

const { getQTVCompositionId } = useQTVCompositionId();

const compositionId = computed(() => {
	const sourceTitleID = props.title.id;
	const similarTitleID = similarTitle.value?.node.id ?? 'nsid';
	const shortName = providerShortName.value === 'amp' ? 'prv' : providerShortName.value;

	return getQTVCompositionId(shortName, sourceTitleID, similarTitleID, 'sqtv');
});

const similarityScore = computed(() =>
	isSimilarTitlesEdge(similarTitle.value) ? similarTitle.value.similarityScore : null
);

const recommenderContext = computed(() => {
	const seasonNumber = props.title.__typename === 'Season' ? props.title.content.seasonNumber : null;
	const titleId = props.title.__typename === 'Season' ? props.title.show.id : props.title.id;

	return TrackingHelper.getRecommenderContext(
		props.source,
		titleId,
		seasonNumber,
		null,
		compositionId.value,
		null,
		null,
		null,
		similarityScore.value
	);
});

const similarTitleContext = computed(() => {
	if (!similarTitle.value) return null;
	return new SnowplowTitleContextGraphql(
		similarTitle.value.node.objectId,
		similarTitle.value.node.objectType,
		null,
		null,
		null,
		0
	);
});

const { setPrimeSQTVExpTags } = usePrimeSQTVExp();

const clickoutUrl = computed(() => {
	if (!offer.value) return null;

	if (props.source === 'sr_sqtv') {
		return new ClickoutUrl(offer.value)
			.addContexts(props.additionalContexts)
			.addContexts(titleContext.value)
			.skipRedirect()
			.set('uct_country', country.value)
			.updateContext('ClickoutContextGraphql', 'clickoutType', sqtvClickoutType.value)
			.updateContext('ClickoutContextGraphql', 'placement', 'sr_buybox')
			.setAdIds(adServerIds.value);
	}

	const clickoutUrl = new ClickoutUrl(offer.value)
		.set('uct_country', country.value)
		.set('uct_buybox', BuyboxParam.Normal)
		.set('uct_ft', '1')
		.addContexts(similarTitleContext.value)
		.setPageType(similarTitle.value?.node.objectType ?? props.title.objectType)
		.setTitleSourceId(similarTitle.value?.node.id ?? null)
		.setForTracking('offerLocation', OfferLocation.FREE_TRIAL)
		.setTitleSourceId(similarTitle.value?.node?.id ?? null)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
		.updateContext('ClickoutContextGraphql', 'swiperIndex', props.swiperIndex)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.addContexts(recommenderContext.value)
		.setCanary(xServeTrack.value)
		.setAdIds(adServerIds.value);

	// Prime Associates - Impact, and all other providers already handled by UCT2
	if (['amp', 'prv'].includes(providerShortName.value) && !primeImpactCountries.includes(country.value)) {
		const suffix = getPrimeAssociatesSuffix(country.value);
		const type = similarTitle.value?.node.objectType === 'MOVIE' ? 'mov' : 'sho';
		const tag = `amazon-${country.value.toLowerCase()}-sqtv-${type}-${suffix}`;

		clickoutUrl
			.set('uct_ctag', tag)
			.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	clickoutUrl.apply(u => setPrimeSQTVExpTags(u, 'sqtv'));

	return clickoutUrl;
});

const titleTags = computed(() => (props.title.content.tags ?? []).map(tag => tag.translatedName));
const titleGenres = computed(() => props.title.content.genres.map(el => el.translation));

const tags = computed(() => {
	if (props.customTags) return props.customTags;

	const similarTitleTags = similarTitle.value?.node?.content?.tags?.map(tag => tag.translatedName) ?? [];

	const commonTags = titleTags.value.filter(tag => similarTitleTags.includes(tag));

	let commonGenres = [] as string[];
	if (commonTags.length < 2) {
		const similarTitleGenres = similarTitle.value?.node?.content?.genres?.map(tag => tag.translation) ?? [];

		commonGenres = titleGenres.value.filter(genre => similarTitleGenres.includes(genre));
	}

	const freeTag =
		offer.value.monetizationType === MonetizationType.Free ? tc('WEBAPP_FREE') : tc('WEBAPP_FREE_TRIAL_TAG');

	return [
		...commonTags,
		...commonGenres,
		// EXP_JWTV_TVOD
		props.hideFreeTrialTag ? null : freeTag,
	]
		.filter(Boolean)
		.slice(0, 3);
});

/* VISIBILITY TRACKING */
const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

const baseContexts = computed(() => [...props.additionalContexts, recommenderContext.value]);
const recommenderImpressionContexts = computed(() =>
	similarTitleContext.value ? [...baseContexts.value, similarTitleContext.value] : baseContexts.value
);

const offerImpressionContexts = computed(() =>
	titleContext.value ? [...baseContexts.value, titleContext.value] : baseContexts.value
);

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: offerImpressionContexts,
	groupId: 'sqtv',
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible) return;

	if (!mountImpressionPixel.value) {
		mountImpressionPixel.value = true;
	}

	// ML Recommender (similar titles) impression tracking
	TrackingHelper.trackEvent(
		'recommender_feature',
		{
			action: 'impression',
			value: 1,
			nonInteraction: true,
		},
		recommenderImpressionContexts.value
	);

	if (clickoutUrl.value) {
		onIntersection(clickoutUrl.value, isVisible);
	}
}
/* VISIBILITY TRACKING */

function imageUrl(titlePosterUrl: string | null | undefined, type: 'poster' | 'backdrop' = 'poster') {
	if (!titlePosterUrl) {
		return undefined;
	}
	return Vue.filter('Thumbor')({
		url: titlePosterUrl,
		imageType: type,
	} as ThumborOptions)?.replace('{format}', 'jpg');
}
//EXP_JWTV_TVOD
const { openRentalModal } = useRentalModal();

function handleClickout(e: Event) {
	if (offer.value.package.shortName === 'jwt') {
		e.preventDefault();
		if (isJWTVRentalOffer(offer.value)) {
			openRentalModal({
				title: similarTitle.value?.node,
				offer: offer.value,
				clickoutUrl: clickoutUrl.value!,
				overrideRedirectUrl: true,
			});
			return;
		} else {
			ModalHelper.openModal(
				() => import('@/ui-components/video-player-modals/JwTVModal.vue'),
				{
					offer: offer.value,
					title: similarTitle.value?.node,
					additionalContexts: getClickoutUrlContextsArray(clickoutUrl.value),
				},
				{ cssClass: 'trailer-modal' },
				'',
				true,
				[]
			);
		}
	}
}
//EXP_JWTV_TVOD

// Type guard, since we now use this component to handle customTargetTitle
function isSimilarTitlesEdge(value: unknown): value is SimilarTitlesEdge & { similarityScore?: Maybe<number> } {
	return (
		value != null && typeof value === 'object' && '__typename' in value && value.__typename === 'SimilarTitlesEdge'
	);
}

return { __sfc: true,props, country, webLocale, xServeTrack, tc, sqtvClickoutType, clickoutType, placement, titleContext, filterOption, adServerIds, mountImpressionPixel, providerShortName, isDisneyPlusSQTV, disneyPlusImpressionPixel, _similarTitle, loading, sqtvBannerAndDisclaimer, similarTitle, scores, excludedScores, sourcePosterUrl, posterUrl, backdropUrl, votesNumber, offer, jwRawEntityId, getQTVCompositionId, compositionId, similarityScore, recommenderContext, similarTitleContext, setPrimeSQTVExpTags, clickoutUrl, titleTags, titleGenres, tags, observeVisibilityOptions, baseContexts, recommenderImpressionContexts, offerImpressionContexts, onIntersection, onVisibilityChanged, imageUrl, openRentalModal, handleClickout, isSimilarTitlesEdge, ImpressionPixel, PictureComp, TitleScoreList, ProviderIcon, PromotionRowCTA, ClickoutAnchor, DisclaimerButton }
}

})